if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector(
        '.js-results-container'
      );
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector(
        '.js-message-error'
      );
      this.errorMessageText =
        this.errorMessageContainer.querySelector('[data-text]');
      this.timeout = null;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');

      // scoped to this instance, so a second predictive-search on the page
      // (e.g. header inline bar + mobile drawer) can't grab each other's elements
      this.inlineWrapper = this.closest('.header__search');
      const searchDrawerContent = this.closest('.drawer__content--search');
      const searchSuggestionsContainer = searchDrawerContent || this.inlineWrapper;
      const searchSuggestions = searchSuggestionsContainer
        ? searchSuggestionsContainer.querySelector('.search-drawer__suggestions')
        : null;
      this.searchSuggestions = searchSuggestions;

      this.input.addEventListener('input', e => {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          const searchTerm = this.input.value.trim();

          if (searchTerm.length <= 0) {
            this.close();
            if(window.innerWidth < 990 && searchDrawerContent) {
              searchDrawerContent.style.height = 'auto';
            }
            if(searchSuggestions) {
              searchSuggestions.style.display = 'block';
            }
            this.updateSearchOpenState();
            return;
          }

          this.isOpen = true;
          this.getSearchResults(searchTerm);
          if(window.innerWidth <= 990 && searchDrawerContent) {
            searchDrawerContent.style.height = '100%';
          }
          if(searchSuggestions) {
            searchSuggestions.style.display = 'none';
          }
          this.updateSearchOpenState();
        }, 300);
      });

      this.input.addEventListener('focus', () => {
        if (this.inlineWrapper && !this.input.value && searchSuggestions) {
          searchSuggestions.style.display = 'block';
          this.updateSearchOpenState();
        }
      });

      this.input.addEventListener('keydown', e => {
        const isEscape = e.key === 'Escape';

        if (!isEscape) {
          return;
        }

        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          } else if (searchSuggestions) {
            searchSuggestions.style.display = 'none';
            this.updateSearchOpenState();
          }

          return;
        }

        this.close();
      });

      this.btnClearText.addEventListener('click', e => {
        this.close();
        if (searchDrawerContent) {
          searchDrawerContent.style.height = 'auto';
        }
        if(searchSuggestions) {
          searchSuggestions.style.display = 'block';
        }
        this.updateSearchOpenState();
      });

      document.addEventListener('click', e => {
        if (this.contains(e.target)) {
          return;
        }

        if (this.isOpen) {
          this.hide();
        }

        if (this.inlineWrapper && searchSuggestions) {
          searchSuggestions.style.display = 'none';
        }
        this.updateSearchOpenState();
      });

      // event delegation: the results close button is re-created on every
      // AJAX response, so it can't be bound directly like suggestionsClose
      this.addEventListener('click', e => {
        if (!e.target.closest('.search-suggestions__close')) {
          return;
        }

        if (this.isOpen) {
          this.close();
        }

        // close() refocuses the (now-empty) input, which re-triggers the
        // focus listener above and reopens searchSuggestions -- force it
        // shut afterward so both panels close together, then blur so a
        // later click into the input fires a fresh focus event and can
        // reopen suggestions again.
        if (searchSuggestions) {
          searchSuggestions.style.display = 'none';
        }
        this.input.blur();
        this.updateSearchOpenState();
      });
    }

    updateSearchOpenState() {
      if (!this.inlineWrapper) {
        return;
      }

      const suggestionsVisible = !!(
        this.searchSuggestions && this.searchSuggestions.style.display === 'block'
      );

      this.inlineWrapper.classList.toggle('is-search-open', suggestionsVisible || this.isOpen);
    }

    // Shopify's predictive search API returns far fewer matches per type when
    // multiple resource types are requested together than when each type is
    // requested alone -- fetch products and search-suggestions (collections +
    // queries) separately, then stitch the fragments together, so counts
    // match what a single-type request actually returns.
    async fetchSection(searchTerm, resourceTypes) {
      const response = await fetch(
        `${Shopify.routes.root}search/suggest?q=${searchTerm}&resources[type]=${resourceTypes}&section_id=predictive-search`
      );

      if (!response.ok) {
        throw new Error(response.status);
      }

      const text = await response.text();

      return new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
    }

    async getSearchResults(searchTerm) {
      try {
        const [productsDoc, suggestionsDoc] = await Promise.all([
          this.fetchSection(searchTerm, 'product'),
          this.fetchSection(searchTerm, 'collection,query'),
        ]);

        if (!this.isOpen) {
          return;
        }

        const suggestionsBlock = suggestionsDoc.querySelector('#predictive-search-suggestions');
        const closeButton = productsDoc.querySelector('.search-suggestions__close');

        if (suggestionsBlock && closeButton) {
          closeButton.insertAdjacentElement('afterend', suggestionsBlock);
        }

        const collectionsBlock = suggestionsDoc.querySelector('#predictive-search-collections');
        const collectionsSlot = productsDoc.querySelector('#predictive-search-collections-slot');

        if (collectionsBlock && collectionsSlot) {
          collectionsSlot.replaceWith(collectionsBlock);
        }

        this.resultsContainer.innerHTML = productsDoc.innerHTML;
        this.open();

        // new added product cards - quick cart triggers - set event listeners
        document.querySelector('quick-cart-drawer')?.initTriggers();

      } catch (error) {
        this.errorMessageText.textContent = error.message;
        this.errorMessageContainer.classList.remove('hidden');
      }
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.btnSubmit.setAttribute('tabindex', '-1');
      this.btnClearText.removeAttribute('disabled');
      this.updateSearchOpenState();
    }

    hide() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.updateSearchOpenState();
    }

    close() {
      this.hide();
      this.input.value = '';
      this.input.focus();
      this.btnSubmit.removeAttribute('tabindex');
      this.btnClearText.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
